import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';

import { Button, FieldLocationAutocompleteInput, Form, Modal } from '../../components';
import { FormattedMessage } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import { updateServicesAndDeals } from '../../util/api';

import css from './BusinessLocationUpdateModal.module.css';

const identity = v => v;

/**
 * Blocking modal shown on the Payout page for merchants whose business listing address
 * predates the Aug 2026 Mapbox->Google Maps switch (publicData.gmapsLocation !== true).
 * Re-uses the exact same update mechanism as TapPayoutForm.js's post-create address sync:
 * update the business listing's own geolocation/location (preserving building/city/
 * country/postalCode), then propagate to linked service/product/deal/staff listings via
 * the existing update-services-and-deals endpoint.
 *
 * No dismiss — merchants must save a real, selected address before they can use the rest
 * of the Payout page.
 */
const BusinessLocationUpdateModal = props => {
  const {
    isOpen,
    intl,
    currentUserListing,
    config,
    onUpdateListing,
    onManageDisableScrolling,
    onSaved,
  } = props;

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async values => {
    const selectedPlace = values?.business_location?.selectedPlace;
    if (!selectedPlace || !currentUserListing?.id) return;

    setSaving(true);
    setError(null);
    try {
      const { address, origin, city, postalCode } = selectedPlace;
      const existingLocation = currentUserListing?.attributes?.publicData?.location || {};
      // The pin moved, so city / postal code must follow it. Keep the old value
      // only when the picked place doesn't report one.

      await onUpdateListing(
        '',
        {
          id: currentUserListing.id,
          geolocation: origin,
          publicData: {
            location: {
              ...existingLocation,
              address,
              ...(city ? { city } : {}),
              ...(postalCode ? { postalCode } : {}),
            },
            gmapsLocation: true,
          },
        },
        config
      );

      const businessId = currentUserListing.id?.uuid || currentUserListing.id;
      try {
        await updateServicesAndDeals({ businessId });
      } catch (linkedListingsError) {
        // Best-effort — the business listing itself is already saved above, and that's
        // what unblocks the merchant. Don't fail the whole modal over this.
        console.error(
          '[BusinessLocationUpdateModal] Failed to sync address to linked listings',
          linkedListingsError
        );
      }

      onSaved();
    } catch (submitError) {
      console.error('[BusinessLocationUpdateModal] Failed to save business address', submitError);
      setError(
        intl.formatMessage({
          id: 'BusinessLocationUpdateModal.saveError',
          defaultMessage: 'Could not save your business address. Please try again.',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      id="BusinessLocationUpdateModal"
      isOpen={isOpen}
      onClose={() => {}}
      disableClose
      showCloseBtn={false}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
      contentClassName={css.modalContent}
    >
      <h2 className={css.title}>
        <FormattedMessage
          id="BusinessLocationUpdateModal.title"
          defaultMessage="Confirm your business address"
        />
      </h2>
      <p className={css.description}>
        <FormattedMessage
          id="BusinessLocationUpdateModal.description"
          defaultMessage="We've upgraded how we verify business locations. Please re-enter and confirm your business address to continue to payouts."
        />
      </p>
      <FinalForm
        onSubmit={handleSubmit}
        render={({ handleSubmit: onFormSubmit, invalid, values }) => (
          <Form onSubmit={onFormSubmit}>
            <FieldLocationAutocompleteInput
              className={css.field}
              autoFocus
              name="business_location"
              id="business_location_modal"
              placeholder={intl.formatMessage({
                id: 'BusinessLocationUpdateModal.placeholder',
                defaultMessage: 'Business Address',
              })}
              useDefaultPredictions={false}
              format={identity}
              valueFromForm={values.business_location}
              validate={validators.composeValidators(
                validators.autocompleteSearchRequired(
                  intl.formatMessage({ id: 'TapPayout.requiredField' })
                ),
                validators.autocompletePlaceSelected(
                  intl.formatMessage({
                    id: 'BusinessLocationUpdateModal.addressNotRecognized',
                    defaultMessage: 'Select an address from the list',
                  })
                ),
                validators.autocompletePlaceInSaudiArabia(
                  intl.formatMessage({ id: 'EditListingLocationForm.addressOutsideSaudiArabia' })
                )
              )}
            />
            {error ? <p className={css.error}>{error}</p> : null}
            <Button
              type="submit"
              className={css.submitButton}
              inProgress={saving}
              disabled={invalid || saving}
            >
              <FormattedMessage id="BusinessLocationUpdateModal.save" defaultMessage="Save address" />
            </Button>
          </Form>
        )}
      />
    </Modal>
  );
};

export default BusinessLocationUpdateModal;
